import { useState, useEffect } from "react";
import { Card, CardContent } from "../client/components/ui/card";
import { CardSectionHeader, useIsDark } from "../client/components/ui/card-section-header";
import useTranslation from "../client/hooks/useTranslation";
import { Clock } from "lucide-react";

interface MarketHours {
  name: string;
  timezone: string;
  openTime: string; // HH:mm format
  closeTime: string; // HH:mm format
  openHour: number;
  openMinute: number;
  closeHour: number;
  closeMinute: number;
}

const MARKETS: MarketHours[] = [
  {
    name: "NYSE",
    timezone: "America/New_York",
    openTime: "09:30",
    closeTime: "16:00",
    openHour: 9,
    openMinute: 30,
    closeHour: 16,
    closeMinute: 0,
  },
  {
    name: "LSE",
    timezone: "Europe/London",
    openTime: "08:00",
    closeTime: "16:30",
    openHour: 8,
    openMinute: 0,
    closeHour: 16,
    closeMinute: 30,
  },
  {
    name: "Tokyo",
    timezone: "Asia/Tokyo",
    openTime: "09:00",
    closeTime: "15:00",
    openHour: 9,
    openMinute: 0,
    closeHour: 15,
    closeMinute: 0,
  },
  {
    name: "Euronext",
    timezone: "Europe/Paris",
    openTime: "09:00",
    closeTime: "17:30",
    openHour: 9,
    openMinute: 0,
    closeHour: 17,
    closeMinute: 30,
  },
];

interface MarketStatus {
  name: string;
  timezone: string;
  localTime: string;
  openTime: string;
  closeTime: string;
  isOpen: boolean;
  nextEvent: string;
  nextEventTime: string;
}

// Convert weekday name to number (Monday = 1, Sunday = 7)
const weekdayMap: Record<string, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7,
};

function getMarketStatus(market: MarketHours): MarketStatus {
  const now = new Date();
  
  // Get current time in market timezone
  const localTimeString = now.toLocaleString("en-US", {
    timeZone: market.timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  
  const [localHour, localMinute] = localTimeString.split(":").map(Number);
  const localTimeMinutes = localHour * 60 + localMinute;
  const openTimeMinutes = market.openHour * 60 + market.openMinute;
  const closeTimeMinutes = market.closeHour * 60 + market.closeMinute;
  
  // Get day of week in market timezone
  // Create a date formatter to get the day of week
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: market.timezone,
    weekday: "long",
  });
  const weekdayName = formatter.format(now);
  const dayOfWeek = weekdayMap[weekdayName] || 1;
  
  // Check if it's a weekday (1 = Monday, 7 = Sunday)
  const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
  
  // Check if market is open
  const isOpen =
    isWeekday &&
    localTimeMinutes >= openTimeMinutes &&
    localTimeMinutes < closeTimeMinutes;
  
  // Determine next event
  let nextEvent: string;
  let nextEventTime: string;
  
  if (!isWeekday) {
    // Weekend - next event is Monday open
    const daysUntilMonday = dayOfWeek === 6 ? 2 : 1;
    const nextMonday = new Date(now);
    nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
    nextEvent = "Opens";
    nextEventTime = `${market.openTime} (${formatDate(nextMonday)})`;
  } else if (localTimeMinutes < openTimeMinutes) {
    // Before open
    nextEvent = "Opens";
    nextEventTime = market.openTime;
  } else if (localTimeMinutes >= closeTimeMinutes) {
    // After close - next event is tomorrow open
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone: market.timezone,
      weekday: "long",
    });
    const tomorrowWeekdayName = tomorrowFormatter.format(tomorrow);
    const tomorrowDayOfWeek = weekdayMap[tomorrowWeekdayName] || 1;
    
    if (tomorrowDayOfWeek >= 1 && tomorrowDayOfWeek <= 5) {
      nextEvent = "Opens";
      nextEventTime = `${market.openTime} (${formatDate(tomorrow)})`;
    } else {
      // Weekend - find next Monday
      const daysUntilMonday = 8 - tomorrowDayOfWeek;
      const nextMonday = new Date(tomorrow);
      nextMonday.setDate(nextMonday.getDate() + daysUntilMonday);
      nextEvent = "Opens";
      nextEventTime = `${market.openTime} (${formatDate(nextMonday)})`;
    }
  } else {
    // Currently open - next event is close
    nextEvent = "Closes";
    nextEventTime = market.closeTime;
  }
  
  return {
    name: market.name,
    timezone: market.timezone,
    localTime: localTimeString,
    openTime: market.openTime,
    closeTime: market.closeTime,
    isOpen,
    nextEvent,
    nextEventTime,
  };
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export default function MarketHoursCard({ colSpan = 1 }: { colSpan?: number }) {
  const t = useTranslation();
  const isDark = useIsDark();
  const [marketStatuses, setMarketStatuses] = useState<MarketStatus[]>([]);

  useEffect(() => {
    const updateStatuses = () => {
      const statuses = MARKETS.map(getMarketStatus);
      setMarketStatuses(statuses);
    };

    updateStatuses();
    const interval = setInterval(updateStatuses, 60000);
    return () => clearInterval(interval);
  }, []);

  const isExpanded = colSpan > 1;

  if (isExpanded) {
    return (
      <Card className="h-full flex flex-col">
        <CardSectionHeader
          label={t.dashboard.marketHours.title}
          isDark={isDark}
          compact
          rightContent={<Clock className="h-4 w-4 text-muted-foreground" />}
        />
        <CardContent className="px-4 pb-4 pt-2 flex-1 grid grid-cols-2 gap-3 content-around">
          {marketStatuses.map((market) => (
            <div
              key={market.name}
              className="flex items-start gap-3 p-3 rounded-lg bg-muted"
            >
              <div className="flex flex-col items-center gap-1 pt-0.5">
                <div className={`w-2.5 h-2.5 rounded-full ${market.isOpen ? "bg-success" : "bg-gray-400"}`} />
              </div>
              <div className="flex flex-col min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-bold text-sm leading-tight">{market.name}</span>
                  <span className={`text-xs font-semibold leading-tight ${market.isOpen ? "text-success" : "text-muted-foreground"}`}>
                    {market.isOpen ? t.dashboard.marketHours.open : t.dashboard.marketHours.closed}
                  </span>
                </div>
                <span className="text-lg font-mono font-semibold leading-tight mt-0.5 text-foreground">
                  {market.localTime}
                </span>
                <span className="text-[10px] text-muted-foreground leading-tight mt-1">
                  {market.isOpen ? `${t.dashboard.marketHours.closesAt} ${market.closeTime}` : `${t.dashboard.marketHours.opensAt} ${market.openTime}`}
                </span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.marketHours.title}
        isDark={isDark}
        compact
        rightContent={<Clock className="h-4 w-4 text-muted-foreground" />}
      />
      <CardContent className="px-3 pb-3 pt-1 flex-1 grid grid-cols-2 gap-x-2 gap-y-1 content-around">
        {marketStatuses.map((market) => (
          <div key={market.name} className="flex items-center gap-1.5 min-w-0">
            <div className={`w-2 h-2 rounded-full shrink-0 ${market.isOpen ? "bg-success" : "bg-gray-400"}`} />
            <div className="flex flex-col min-w-0">
              <div className="flex items-baseline gap-1">
                <span className="font-semibold text-xs leading-tight shrink-0">{market.name}</span>
                <span className={`text-[10px] font-semibold leading-tight ${market.isOpen ? "text-success" : "text-muted-foreground"}`}>
                  {market.isOpen ? t.dashboard.marketHours.open : t.dashboard.marketHours.closed}
                </span>
              </div>
              <span className="text-[10px] text-muted-foreground font-mono leading-tight">{market.localTime}</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

